"use client";

import { useTranslations } from "next-intl";
import { registerElectionErrors } from "@/hooks/dao/voting/registerElectionErrors";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

const TABS = [
  {
    labelKey: "election",
    href: "/info/dao",
    match: (path: string) => path === "/info/dao",
  },
  {
    labelKey: "actions",
    href: "/info/dao/proposals",
    match: (path: string) => path.startsWith("/info/dao/proposals"),
  },
] as const;

export const DaoTabsNav: React.FC = () => {
  const pathname = usePathname();
  const t = useTranslations("dao");

  // Register Election.sol revert → localized message mappings once.
  // Previously lived in DaoTabs.tsx (now deleted); without this call, every
  // vote/withdraw revert would surface its raw English revert reason instead
  // of the translated copy (see registerElectionErrors.ts for the mapping
  // table). Passing the `dao` translator keeps the error copy in the message
  // catalogs (dao.voting.errors.*) while the handler self-guards against
  // double-registration.
  registerElectionErrors(t);

  return (
    <div
      role="tablist"
      className="flex w-full items-stretch gap-2 rounded-xs border-2 border-stroke-light p-1"
    >
      {TABS.map((tab) => {
        const active = tab.match(pathname);
        return (
          <Link
            key={tab.href}
            href={tab.href}
            role="tab"
            aria-selected={active}
            className={cn(
              "flex flex-1 items-center justify-center rounded-xs px-3 py-1 text-base font-semibold leading-[1.4] transition-colors",
              active
                ? "bg-bg-tertiary text-text-primary"
                : "text-text-disabled hover:text-text-secondary",
            )}
          >
            {t(`tabs.${tab.labelKey}`)}
          </Link>
        );
      })}
    </div>
  );
};
